<script>
	import { onMount } from 'svelte';
	import { marked } from 'marked';

	const PROJECT = 'paxapy/moe';
	const REF = 'main';
	const API_BASE = `https://gitlab.com/api/v4/projects/${encodeURIComponent(PROJECT)}`;
	const CACHE_KEY = 'nyctou:moe:index:v1';
	const CACHE_TTL_MS = 10 * 60 * 1000;
	const routeBase = "moe"

	export let routePath = '';

	let entries = [];
	let listLoading = false;
	let listError = '';
	let mdLoading = false;
	let mdError = '';
	let mdHtml = '';
	let mdPath = '';

	$: grouped = groupEntries(entries);
	$: routeInfo = parseRoute(routePath);
	$: if (routeInfo.fullPath) loadMarkdown(routeInfo.fullPath);
	$: if (!routeInfo.fullPath) clearMarkdown();

	function clearMarkdown() {
		mdHtml = '';
		mdPath = '';
		mdError = '';
		mdLoading = false;
	}

	function readCache() {
		try {
			const raw = localStorage.getItem(CACHE_KEY);
			if (!raw) return null;
			const cached = JSON.parse(raw);
			if (!cached || !cached.entries || !cached.at) return null;
			if (Date.now() - cached.at > CACHE_TTL_MS) return null;
			return cached.entries;
		} catch (error) {
			return null;
		}
	}

	function writeCache(list) {
		try {
			localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), entries: list }));
		} catch (error) {
			return;
		}
	}

	async function loadIndex(force = false) {
		listLoading = true;
		listError = '';
		const cached = force ? null : readCache();
		if (cached && !force) {
			entries = cached;
			listLoading = false;
			return;
		}
		try {
			let page = 1;
			let items = [];
			while (true) {
				const url = `${API_BASE}/repository/tree?ref=${REF}&recursive=true&per_page=100&page=${page}`;
				const response = await fetch(url);
				if (!response.ok) throw new Error('GitLab listing failed');
				const data = await response.json();
				items = items.concat(data);
				const next = response.headers.get('x-next-page');
				if (!next) break;
				page = Number(next);
			}
			entries = items
				.filter((item) => item.type === 'blob' && item.path.endsWith('.md'))
				.map((item) => {
					const match = item.path.match(/^(\d{4})\/(\d{1,2})\/(.+)\.md$/);
					if (!match) return null;
					const [, year, month, name] = match;
					return {
						path: item.path,
						year,
						month,
						monthNum: Number(month),
						name
					};
				})
				.filter(Boolean)
				.sort((a, b) => {
					if (a.year !== b.year) return b.year.localeCompare(a.year);
					if (a.monthNum !== b.monthNum) return b.monthNum - a.monthNum;
					return b.path.localeCompare(a.path);
				});
			writeCache(entries);
		} catch (error) {
			listError = error.message || 'Failed to load Markdown list';
		} finally {
			listLoading = false;
		}
	}

	async function loadMarkdown(path) {
		if (!path || mdPath === path) return;
		mdLoading = true;
		mdError = '';
		mdHtml = '';
		mdPath = path;
		try {
			const encodedPath = encodeURIComponent(path);
			const url = `${API_BASE}/repository/files/${encodedPath}/raw?ref=${REF}`;
			const response = await fetch(url);
			if (!response.ok) throw new Error('Markdown fetch failed');
			const text = await response.text();
			mdHtml = marked.parse(text);
		} catch (error) {
			mdError = error.message || 'Failed to load Markdown';
		} finally {
			mdLoading = false;
		}
	}

	function displayName(entry) {
		return entry.name.replace(/[-_]+/g, ' ');
	}

	function toHash(path) {
		return `#/${routeBase}/${encodeURIComponent(path)}`;
	}

	function toShortHash(year, month, slug) {
		const safeYear = encodeURIComponent(year);
		const safeMonth = encodeURIComponent(String(Number(month)));
		const safeSlug = encodeURIComponent(slug);
		return `#/${routeBase}/${safeYear}/${safeMonth}/${safeSlug}`;
	}

	function parseRoute(path) {
		if (!path) return { year: '', month: '', slug: '', fullPath: '' };
		const decoded = decodeURIComponent(path);
		if (decoded.endsWith('.md')) {
			const match = decoded.match(/^(\d{4})\/(\d{1,2})\/(.+)\.md$/);
			const year = match ? match[1] : '';
			const month = match ? match[2] : '';
			const slug = match ? match[3] : '';
			return { year, month, slug, fullPath: decoded };
		}
		const parts = path.split('/').filter(Boolean).map((part) => decodeURIComponent(part));
		const year = parts[0] && /^\d{4}$/.test(parts[0]) ? parts[0] : '';
		const month = parts[1] && /^\d{1,2}$/.test(parts[1]) ? parts[1] : '';
		const slug = parts[2] ? parts[2] : '';
		const fullPath = year && month && slug
			? `${year}/${month}/${slug}.md`
			: '';
		return { year, month, slug, fullPath };
	}

	function groupEntries(list) {
		const map = new Map();
		for (const entry of list) {
			const key = `${entry.year}-${entry.month}`;
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(entry);
		}
		return Array.from(map.entries())
			.map(([key, items]) => {
				const [year, month] = key.split('-');
				return { year, month, items };
			})
			.sort((a, b) => {
				if (a.year !== b.year) return b.year.localeCompare(a.year);
				return Number(b.month) - Number(a.month);
			});
	}

	onMount(() => {
		loadIndex();
	});

	$: years = Array.from(new Set(entries.map((entry) => entry.year))).sort((a, b) => b.localeCompare(a));
	$: monthsForYear = routeInfo.year
		? Array.from(new Set(entries.filter((entry) => entry.year === routeInfo.year)
			.map((entry) => entry.month)))
			.sort((a, b) => Number(b) - Number(a))
		: [];
	$: filesForMonth = routeInfo.year && routeInfo.month
		? entries.filter((entry) => entry.year === routeInfo.year && entry.month === routeInfo.month)
			.sort((a, b) => b.path.localeCompare(a.path))
		: [];
</script>

<section class="markdown">
	<aside class="list">
		<div class="list-top">
			<h2>moe</h2>
			<button class="ghost" on:click={() => loadIndex(true)} title="refresh list">
				r
			</button>
		</div>
		<nav class="crumbs">
			<a href={`#/${routeBase}`}>all</a>
			{#if routeInfo.year}
				<span>·</span>
				<a href={`#/${routeBase}/${routeInfo.year}`}>{routeInfo.year}</a>
			{/if}
			{#if routeInfo.year && routeInfo.month}
				<span>·</span>
				<a href={`#/${routeBase}/${routeInfo.year}/${Number(routeInfo.month)}`}>{routeInfo.month}</a>
			{/if}
		</nav>
		{#if listLoading}
			<p class="status">loading list...</p>
		{:else if listError}
			<p class="status error">{listError}</p>
		{:else if entries.length === 0}
			<p class="status">no markdown yet</p>
		{:else if !routeInfo.year}
			<ul>
				{#each years as year}
					<li>
						<a href={`#/${routeBase}/${year}`}>{year}</a>
					</li>
				{/each}
			</ul>
		{:else if routeInfo.year && !routeInfo.month}
			<ul>
				{#each monthsForYear as month}
					<li>
						<a href={`#/${routeBase}/${routeInfo.year}/${Number(month)}`}>{month}</a>
					</li>
				{/each}
			</ul>
		{:else if routeInfo.year && routeInfo.month}
			<ul>
				{#each filesForMonth as entry}
					<li class:selected={entry.path === mdPath}>
						<a href={toShortHash(entry.year, entry.month, entry.name)}>{displayName(entry)}</a>
					</li>
				{/each}
			</ul>
		{:else}
			{#each grouped as group}
				<h3>{group.year}/{group.month}</h3>
				<ul>
					{#each group.items as entry}
						<li class:selected={entry.path === mdPath}>
							<a href={toHash(entry.path)}>{displayName(entry)}</a>
						</li>
					{/each}
				</ul>
			{/each}
		{/if}
	</aside>
	<article class="viewer">
		{#if mdLoading}
			<p class="status">loading text...</p>
		{:else if mdError}
			<p class="status error">{mdError}</p>
		{:else if mdHtml}
			<div class="content">{@html mdHtml}</div>
		{:else}
			{#if !routeInfo.year}
				<p class="status">pick a year</p>
			{:else if routeInfo.year && !routeInfo.month}
				<p class="status">pick a month</p>
			{:else}
				<p class="status">pick a text</p>
			{/if}
		{/if}
	</article>
</section>

<style>
	.markdown {
		display: grid;
		grid-template-columns: minmax(200px, 260px) minmax(0, 1fr);
		gap: 24px;
		align-items: start;
	}
	.list-top {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 6px;
	}
	.list h2 {
		margin: 0;
	}
	.crumbs {
		display: flex;
		gap: 6px;
		align-items: center;
		margin-bottom: 12px;
		font-size: 0.95rem;
	}
	.crumbs span {
		opacity: 0.6;
	}
	.list h3 {
		margin: 18px 0 6px;
		font-size: 1rem;
	}
	.list ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	.list li {
		margin-bottom: 6px;
	}
	.list li.selected a {
		text-decoration: underline;
	}
	.viewer {
		min-height: 320px;
	}
	.status {
		opacity: 0.7;
	}
	.status.error {
		color: #7a2d2d;
	}
	.ghost {
		border: 1px solid currentColor;
		background: transparent;
		color: inherit;
		padding: 2px 8px;
		cursor: pointer;
	}
	.content {
		padding: 23px;
		max-width: 890px;
		max-height: 89vh;
		overflow-y: scroll;
		/* font-family: 'Noto Serif'; */
		background: rgba(255, 255, 255, 0.84);
	}
	.content :global(h1),
	.content :global(h2),
	.content :global(h3),
	.content :global(h4) {
		margin-top: 1.2em;
		margin-bottom: 0.4em;
	}
	.content :global(h1) {
		font-size: 1.6rem;
	}
	.content :global(p) {
		line-height: 1.6;
		margin: 0 0 0.9em;
	}
	.content :global(blockquote) {
		margin: 0 0 1em;
		padding-left: 12px;
		border-left: 2px solid currentColor;
		opacity: 0.85;
	}
	.content :global(pre) {
		overflow-x: auto;
		padding: 12px;
		border: 1px solid currentColor;
	}
	.content :global(code) {
		font-family: "IBM Plex Mono", "Fira Mono", "Menlo", monospace;
		font-size: 0.95em;
	}
	.content :global(ul),
	.content :global(ol) {
		padding-left: 1.2em;
		margin: 0 0 1em;
	}
	.content :global(a) {
		text-decoration: underline;
	}
	@media (max-width: 720px) {
		.markdown {
			grid-template-columns: 1fr;
		}
	}
</style>
